import { useState, useEffect } from 'react';

const CACHE_KEY = 'episodes_cache';
const CACHE_TIMESTAMP_KEY = 'episodes_cache_timestamp';
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export function useEpisodes() {
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchEpisodes = async () => {
      // Check localStorage cache first
      const cached = localStorage.getItem(CACHE_KEY);
      const cachedTimestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);

      if (cached && cachedTimestamp && Date.now() - parseInt(cachedTimestamp) < CACHE_TTL) {
        try {
          const parsedCache = JSON.parse(cached);
          setEpisodes(parsedCache);
          setLoading(false);
          return;
        } catch (e) {
          // Invalid cache, continue to fetch
        }
      }

      try {
        const response = await fetch('/.netlify/functions/episodes');

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        const episodeList = data.episodes || [];

        setEpisodes(episodeList);
        setLoading(false);

        // Update localStorage cache
        localStorage.setItem(CACHE_KEY, JSON.stringify(episodeList));
        localStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
      } catch (err) {
        console.error('Error fetching episodes:', err);
        setError(err);
        setLoading(false);

        // Try to use stale cache as fallback
        if (cached) {
          try {
            setEpisodes(JSON.parse(cached));
          } catch (e) {
            // Cache is corrupted
          }
        }
      }
    };

    fetchEpisodes();
  }, []);

  return { episodes, loading, error };
}

// Helper function to get episodes by tag (tag should include # prefix)
export function getEpisodesByTag(episodes, tag) {
  const normalizedTag = tag.startsWith('#') ? tag.toLowerCase() : `#${tag.toLowerCase()}`;
  return episodes.filter(ep => ep.tags && ep.tags.includes(normalizedTag));
}

// Helper function to get a single episode by ID (slug)
export function getEpisodeById(episodes, id) {
  return episodes.find(ep => ep.id === id);
}

// Helper function to get the latest episode
export function getLatestEpisode(episodes) {
  if (!episodes || episodes.length === 0) return null;
  return episodes.reduce((latest, ep) =>
    new Date(ep.date) > new Date(latest.date) ? ep : latest
  );
}

// Helper function to get all unique tags from episodes
export function getAllTags(episodes) {
  const tagSet = new Set();
  episodes.forEach(ep => {
    if (ep.tags) {
      ep.tags.forEach(tag => tagSet.add(tag));
    }
  });
  return [...tagSet].sort();
}
